import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Link, useParams } from 'react-router-dom'
import { Button } from '@/shared/ui/Button'
import { Pagination } from '@/shared/ui/Pagination'
import { Empty, Panel } from '@/shared/ui/Panel'
import { Strip } from '@/shared/ui/Strip'
import { Tile } from '@/shared/ui/Tile'
import type { TransactionResponse } from '@/shared/api/types'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import { explain } from '@/shared/lib/errors'
import { crypto, shortAddress } from '@/shared/lib/format'
import { useMeta } from '@/shared/lib/hooks/useMeta'
import { useStores } from '@/stores/RootStore'
import { AccountDialogs } from './AdminAccountsPage'
import { AdminIntegration } from './AdminIntegration'
import page from './Page.module.css'
import styles from './Admin.module.css'

/**
 * Карточка мерчанта.
 *
 * Раньше кнопка «История» в списке аккаунтов вела на `/app/history`
 * с параметром `account`, которого нет ни в API, ни в фильтрах: админ
 * видел СВОЮ историю и мог принять её за историю мерчанта. Для разбора
 * жалобы это худшее, что может показать экран.
 */
export const AdminAccountPage = observer(function AdminAccountPage() {
  const { accountId } = useParams<{ accountId: string }>()
  const { admin: store } = useStores()
  const account = store.card.value
  useMeta(account ? account.email : 'Аккаунт')

  useEffect(() => {
    if (accountId) store.openCard(accountId)
  }, [accountId, store])

  if (!accountId) return null

  return (
    <div className={page.wide}>
      <header className={page.head}>
        <Link className={styles.crumb} to="/app/admin/accounts">← Аккаунты</Link>
        <h1 className={styles.h1}>{account?.email ?? 'Аккаунт'}</h1>
      </header>

      {store.card.error && !store.card.loaded ? (
        <Strip tone="bad"
               aside={<Button variant="secondary" onClick={() => void store.card.load()}>Ещё раз</Button>}>
          {explain(store.card.error)}
        </Strip>
      ) : null}

      {account ? (
        <>
          <div className={styles.tiles}>
            <Tile label="Роль" value={account.role === 'admin' ? 'Администратор' : 'Пользователь'} />
            <Tile label="Комиссия сервиса" value={`${account.commission_percent}%`} />
            <Tile label="Доступ"
                  value={account.enabled ? 'Активен' : 'Заблокирован'}
                  note={account.twofa_enabled ? '2FA включена' : '2FA отключена'} />
          </div>

          <Panel title="Настройки"
                 actions={<Button variant="secondary" onClick={() => store.openEdit(account)}>Изменить</Button>}>
            <ul className={styles.flags}>
              <li className={styles.flag}>
                <span>Платёжные ссылки</span>
                <span className={account.payment_links_enabled ? styles.yes : styles.no}>
                  {account.payment_links_enabled ? 'Разрешены' : 'Запрещены'}
                </span>
              </li>
              <li className={styles.flag}>
                <span>Комиссию сети платит покупатель</span>
                <span className={account.client_pays_network_fee ? styles.yes : styles.no}>
                  {account.client_pays_network_fee ? 'Да' : 'Нет'}
                </span>
              </li>
              <li className={styles.flag}>
                <span>Вход через Telegram</span>
                <span className={account.telegram_id ? styles.yes : styles.no}>
                  {account.telegram_id ? `Привязан · id ${account.telegram_id}` : 'Не привязан'}
                </span>
              </li>
            </ul>
          </Panel>

          <AdminIntegration accountId={account.id} />
        </>
      ) : null}

      <Panel title="Операции мерчанта">
        {store.cardTx.value && store.cardTx.value.items.length > 0 ? (
          <>
            <ul className={styles.queue}>
              {store.cardTx.value.items.map((tx) => <TxRow key={tx.id} tx={tx} />)}
            </ul>
            <Pagination total={store.cardTx.value.total} limit={store.limit}
                        offset={store.cardTxOffset}
                        onChange={(offset) => store.goCardTx(offset)} />
          </>
        ) : store.cardTx.loaded ? (
          <Empty title="Операций нет" />
        ) : null}
      </Panel>

      <AccountDialogs />
    </div>
  )
})

function TxRow({ tx }: { tx: TransactionResponse }) {
  const coin = coinOf(tx.currency)
  return (
    <li className={styles.queueRow}>
      <span className={styles.queueMain}>
        <span className={styles.queueAmount}>
          {tx.type === 'receive' ? '+' : '−'}{crypto(tx.amount)} {coin.ticker}
        </span>
        <span className={styles.queueMeta}>
          {networkOf(tx.currency)} · {tx.address ? shortAddress(tx.address, 10, 6) : '—'}
        </span>
        <span className={styles.queueMeta}>{when(tx.created_at)}</span>
      </span>
    </li>
  )
}

function when(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date).replace(', ', ' / ')
}
